import { ButtonBase } from '@common/Component/Forms';
import { PAGE_NAME } from '@common/Constants/analytics';
import useWindowDimensions from '@common/Context/useWindowDimensions';
import { updateLabsOnHomeMarkReport } from 'aster-foundation/core/teleconsultant/records/redux/actions';
import moment from 'moment';
import Image from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { REPORT_TIMER_PROGRESS, REPORT_TIMER_READY } from '../../../Common/Assets/images';
import { REPORT_FOOTER, REPORT_STATUS } from '../../../Common/Constants/digitalcare';
import { errorHandling } from '../../../Common/Utility/common-utils';
import { TELECONSULT_ROUTE } from '../../../Common/Utility/constants';
import { labReportCardClick } from '../LabReportAnalytics';
import LabreportCardStyled from './LabreportCardStyled';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const REPORT_SATUS = ['Completed', 'In-Progress'];

const LabreportCard = ({ patientName, patientId, appointment, labReport }) => {
  const router = useRouter();
  const generateURL = useNavigationURL();
  const dispatch = useDispatch();
  const windowSize = useWindowDimensions();

  const navigateToRecords = useCallback(() => {
    router.push({
      pathname: generateURL(`${TELECONSULT_ROUTE}/records/${appointment?.labResults?.userRecordId}`),
      query: {
        code: 'LAB',
        doctorId: appointment?.doctorId,
        speciality: appointment?.speciality,
        screenType: 'lab',
        selectedMonthIndex: 0,
        currentPage: 0,
        referrerPage: PAGE_NAME?.HOME,
      },
    });
  }, [router, appointment]);

  const markReportViewd = useCallback(() => {
    const reqParams = { patientId, userRecordId: appointment?.labResults?.userRecordId };
    dispatch(
      updateLabsOnHomeMarkReport({
        reqParams,
        onSuccess: navigateToRecords,
        onFailure: errorHandling,
      }),
    );
  }, [dispatch, patientId, appointment, navigateToRecords]);

  const handleViewDetails = useCallback(() => {
    labReportCardClick(labReport, appointment);
    if (
      appointment?.labResults?.summary?.totalLabRecordsCount ===
      appointment?.labResults?.summary?.availableLabRecordsCount
    ) {
      markReportViewd();
    } else {
      navigateToRecords();
    }
  }, [labReport, appointment, markReportViewd, navigateToRecords]);

  const getConvertedDate = (aptDate) => moment(aptDate, 'DD/MM/YYYY').format('DD MMM, YYYY');

  const renderReport = ({ name, status }) => (
    <div className="singleReport" key={name?.length}>
      <Image
        src={status === REPORT_STATUS.IN_PROGRESS ? REPORT_TIMER_PROGRESS : REPORT_TIMER_READY}
        width={28}
        height={28}
        alt={status === REPORT_STATUS.IN_PROGRESS ? 'report in progress' : 'report is ready'}
      />
      <span title={name}>{name}</span>
    </div>
  );

  const renderFooter = () => {
    if (appointment?.labResults?.summary?.availableLabRecordsCount === 0) {
      return (
        <div className="message_wrapper">
          <span>{REPORT_FOOTER.MESSAGE}</span>
        </div>
      );
    }
    if (appointment?.labResults?.summary?.availableLabRecordsCount !== 0 && windowSize?.width < 426) {
      return (
        <ButtonBase
          customClass="btn btn-outline-primary btn-sm viewDetailsCTA"
          size="large"
          data-test="Order-Click-button"
          variant="outline-primary"
          text="View Details"
          onClick={handleViewDetails}
        />
      );
    }
    return null;
  };

  return (
    <LabreportCardStyled>
      <div className="labReportCardWrapper">
        <div className="reportHeader">
          <div className="appointmentDetail">
            <div className="patient">
              <div className="patientName">{patientName}</div>
              <div className="docName">{appointment?.doctorName}</div>
            </div>
            <div className="appointmentDate">{getConvertedDate(appointment?.appointmentDate)}</div>
          </div>
          <div className="buttonCTA">
            {appointment?.labResults?.summary?.availableLabRecordsCount !== 0 && windowSize?.width > 425 && (
              <ButtonBase
                customClass="btn btn-outline-primary btn-sm viewDetailsCTA"
                size="large"
                data-test="Order-Click-button"
                variant="outline-primary"
                text="View Details"
                onClick={handleViewDetails}
              />
            )}
          </div>
        </div>
        <div className="reportWrapper">
          {appointment?.labResults?.labTests
            ?.sort((a, b) => REPORT_SATUS.indexOf(a?.status) - REPORT_SATUS.indexOf(b?.status))
            .map(renderReport)}
        </div>
        <div className="report_footer">{renderFooter()}</div>
      </div>
    </LabreportCardStyled>
  );
};

LabreportCard.propTypes = {
  appointment: PropTypes.object,
  labReport: PropTypes.arrayOf({}),
  patientId: PropTypes.string,
  patientName: PropTypes.string,
};

LabreportCard.defaultProps = {
  appointment: {},
  patientName: '',
  patientId: '',
  labReport: [],
};

export default LabreportCard;
